import { useState } from 'react'
import HeaderSamples from './HeaderSamples'

/**
 * FileValidationCard - Display validation status for a single file
 *
 * Shows file name, status indicator, quick stats, and expandable details.
 *
 * @param {string} filename - Name of the file
 * @param {Object} result - File result object with header_validation
 */
function FileValidationCard({ filename, result }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const validation = result.header_validation || {}
  const status = getFileStatus(validation)

  // Count headers with issues (same filter as HeaderSamples)
  const problematicHeadersCount =
    validation.header_samples?.filter((sample) =>
      Object.values(sample.metadata_extracted || {}).some((value) => value === 'Unknown')
    ).length || 0

  return (
    <div className={`border rounded-lg p-4 ${getBorderClass(status)}`}>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <StatusIcon status={status} />
          <span className="font-medium text-gray-900 truncate">{filename}</span>
          <StatusBadge status={status} validation={validation} />
        </div>
        {/* Only show expand button if there are problems to view */}
        {(status === 'warning' || status === 'error') && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-3 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </button>
        )}
      </div>

      {/* Quick stats */}
      <div className="mt-2 text-sm text-gray-600">
        Intersections: {result.intersections?.length || 0} • Movement Records:{' '}
        {result.records_count}
        {problematicHeadersCount > 0 &&
          ` • ${problematicHeadersCount} header${problematicHeadersCount > 1 ? 's' : ''} with issues`}
      </div>

      {/* Expandable details */}
      {isExpanded && (status === 'warning' || status === 'error') && (
        <div className="mt-4 space-y-5 border-t pt-4">
          {/* Show problem headers */}
          {validation.header_samples && validation.header_samples.length > 0 && (
            <HeaderSamples samples={validation.header_samples} />
          )}

          {/* Help text */}
          <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-3">
            <p className="font-medium text-blue-900 mb-1">Need Help?</p>
            <p>
              Headers should follow the two-line format: Line 1 with intersection ID (e.g.,
              &quot;1:&quot;), name, and project/scenario. Line 2 with section type and time period
              (e.g., &quot;AM Peak&quot;). Make sure all required fields are present and properly
              formatted.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Determine file status based on validation
 *
 * Only checks for malformed page headers (headers with "Unknown" metadata).
 * Parser handles sections without headers gracefully, so those don't affect status.
 */
function getFileStatus(validation) {
  // Error: No format detected or file can't be parsed
  if (!validation.format_detected) {
    return 'error'
  }

  // Warning: Page headers have missing/unparseable metadata
  const hasProblematicHeaders = validation.header_samples?.some((sample) =>
    Object.values(sample.metadata_extracted || {}).some((value) => value === 'Unknown')
  )

  if (hasProblematicHeaders) {
    return 'warning'
  }

  return 'success'
}

/**
 * Get border styling class based on status
 */
function getBorderClass(status) {
  switch (status) {
    case 'error':
      return 'border-red-200 bg-red-50'
    case 'warning':
      return 'border-yellow-200 bg-yellow-50'
    default:
      return 'border-green-200 bg-green-50'
  }
}

/**
 * Status icon component
 */
function StatusIcon({ status }) {
  switch (status) {
    case 'error':
      return <span className="text-red-500 text-xl">✕</span>
    case 'warning':
      return <span className="text-yellow-500 text-xl">⚠</span>
    default:
      return <span className="text-green-500 text-xl">✓</span>
  }
}

/**
 * Status badge component
 *
 * Shows validation status based on page header quality only.
 */
function StatusBadge({ status, validation }) {
  if (status === 'success') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
        Valid
      </span>
    )
  }

  if (status === 'error') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700">
        Cannot Parse
      </span>
    )
  }

  // Warning status - count headers with issues
  const problematicHeadersCount =
    validation.header_samples?.filter((sample) =>
      Object.values(sample.metadata_extracted || {}).some((value) => value === 'Unknown')
    ).length || 0

  return (
    <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
      {problematicHeadersCount} malformed header{problematicHeadersCount > 1 ? 's' : ''}
    </span>
  )
}

export default FileValidationCard
